import React, { useEffect, useState } from "react";
import api from "../../api";
import { Pagination, Stack } from "@mui/material";
import { useAlert } from "react-alert";

export default function OnboardingAdmin() {
  const alert = useAlert();
  const [settings, setSettings] = useState(null);
  const [weeksInput, setWeeksInput] = useState(4);
  const [cooldownInput, setCooldownInput] = useState(1440);
  const [savingSettings, setSavingSettings] = useState(false);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const loadSettings = async () => {
    try {
      const resp = await api.getOnboardingAdminSettings();
      setSettings(resp?.settings);
      setWeeksInput(resp?.settings?.demoTradingWeeks ?? 4);
      setCooldownInput(resp?.settings?.submissionCooldownMinutes ?? 1440);
    } catch (_) {}
  };

  const loadUsers = async (p = page) => {
    setLoading(true);
    try {
      const resp = await api.getOnboardingAdminUsers({ page: p, limit });
      setUsers(resp?.rows || []);
      setTotal(resp?.total || 0);
    } catch (_) {
      alert.show("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadUsers(1);
  }, []);

  const handleSaveSettings = async () => {
    if (!weeksInput || weeksInput < 1 || weeksInput > 52) {
      alert.show("Weeks must be between 1 and 52.");
      return;
    }
    setSavingSettings(true);
    try {
      await api.updateOnboardingAdminSettings({ demoTradingWeeks: Number(weeksInput), submissionCooldownMinutes: Number(cooldownInput) });
      alert.show("Settings saved.");
      loadSettings();
    } catch (_) {
      alert.show("Failed to save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handlePageChange = (_, val) => {
    setPage(val);
    loadUsers(val);
  };

  const badge = (val, trueLabel = "Yes", falseLabel = "No", trueColor = "#059669", falseColor = "#9a94ad") => (
    <span style={{
      fontFamily: "monospace", fontSize: 10.5, fontWeight: 800, letterSpacing: ".08em",
      borderRadius: 6, padding: "3px 8px",
      color: val ? trueColor : falseColor,
      background: val ? "rgba(5,150,105,.1)" : "#f5f5f8",
    }}>
      {val ? trueLabel : falseLabel}
    </span>
  );

  return (
    <div style={{ padding: "28px 24px", fontFamily: "'Segoe UI', sans-serif", color: "#141020", maxWidth: 1200 }}>
      <style>{`
        .sz-admin-save:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 16px 36px rgba(109,40,217,.42) !important; filter: brightness(1.07); }
        .sz-admin-save:active:not(:disabled) { transform: translateY(0); }
        .sz-admin-save { transition: all .22s ease; }
      `}</style>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".18em", textTransform: "uppercase", color: "#8b5cf6", marginBottom: 6 }}>Admin Panel</div>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: "-.02em" }}>Onboarding Course Settings</h2>
      </div>

      {/* Settings card */}
      <div style={{
        background: "linear-gradient(135deg,#faf8ff 0%,#f3f0ff 100%)",
        border: "1px solid #e4d9fb", borderRadius: 20,
        padding: "28px 32px", marginBottom: 36, maxWidth: 540,
        boxShadow: "0 4px 24px rgba(124,61,237,.08)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>⚙️</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: "-.01em" }}>Demo Trading Duration</div>
            <div style={{ fontSize: 11.5, color: "#9a94ad", marginTop: 1 }}>Controls how long users must trade before unlock</div>
          </div>
        </div>

        {/* Weeks row */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "16px 18px", marginBottom: 12, border: "1px solid #ede8fb" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#3a3552", marginBottom: 3 }}>Weeks Required</div>
              <div style={{ fontSize: 11.5, color: "#a9a4bd" }}>
                Current: <strong style={{ color: "#7c3aed" }}>{settings?.demoTradingWeeks ?? "—"} week(s) · {(settings?.demoTradingWeeks ?? 0) * 7} days</strong>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="number" min={1} max={52}
                value={weeksInput}
                onChange={(e) => setWeeksInput(e.target.value)}
                style={{ width: 72, padding: "9px 10px", border: "1.5px solid #d8cff6", borderRadius: 10, fontSize: 16, fontWeight: 800, textAlign: "center", outline: "none", background: "#faf8ff", color: "#6d28d9" }}
              />
              <span style={{ fontSize: 12.5, color: "#9a94ad", whiteSpace: "nowrap" }}>× 7 = <strong>{weeksInput * 7} days</strong></span>
            </div>
          </div>
        </div>

        {/* Cooldown row */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "16px 18px", marginBottom: 20, border: "1px solid #ede8fb" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#3a3552", marginBottom: 3 }}>Submission Cooldown</div>
              <div style={{ fontSize: 11.5, color: "#a9a4bd" }}>
                Default: 1440 (24h) · Current: <strong style={{ color: "#7c3aed" }}>{settings?.submissionCooldownMinutes ?? "—"} min(s)</strong>
                <span style={{ marginLeft: 6, background: "#fef3c7", color: "#b45309", borderRadius: 4, padding: "1px 6px", fontSize: 10.5, fontWeight: 700 }}>set 1 for testing</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="number" min={1}
                value={cooldownInput}
                onChange={(e) => setCooldownInput(e.target.value)}
                style={{ width: 80, padding: "9px 10px", border: "1.5px solid #d8cff6", borderRadius: 10, fontSize: 16, fontWeight: 800, textAlign: "center", outline: "none", background: "#faf8ff", color: "#6d28d9" }}
              />
              <span style={{ fontSize: 12.5, color: "#9a94ad", whiteSpace: "nowrap" }}>
                {cooldownInput >= 1440 ? `${(cooldownInput / 1440).toFixed(1)}d` : cooldownInput >= 60 ? `${(cooldownInput / 60).toFixed(1)}h` : `${cooldownInput}m`}
              </span>
            </div>
          </div>
        </div>

        <button
          className="sz-admin-save"
          disabled={savingSettings}
          onClick={handleSaveSettings}
          style={{
            width: "100%", padding: "12px", borderRadius: 12, border: "none", cursor: savingSettings ? "not-allowed" : "pointer",
            fontWeight: 800, fontSize: 14, letterSpacing: ".04em",
            background: savingSettings ? "#c4b5fd" : "linear-gradient(135deg,#8b5cf6,#6d28d9)",
            color: "#fff", boxShadow: savingSettings ? "none" : "0 8px 20px rgba(109,40,217,.3)",
            transition: "all .2s ease",
          }}
        >
          {savingSettings ? "Saving…" : "💾  Save Settings"}
        </button>
      </div>

      {/* Users table */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>User Onboarding Progress</h3>
        <span style={{ fontFamily: "monospace", fontSize: 12, color: "#9a94ad" }}>{total} users</span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: "#f6f5fb" }}>
              {["Name", "Email", "Beginner Videos", "Advance Videos", "Demo Trades", "Wins", "Losses", "Win Rate", "Unlocked", "Skipped"].map((h) => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 800, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase", color: "#7c3aed", borderBottom: "1px solid #eeebf6", whiteSpace: "nowrap" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: "#9a94ad" }}>Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: "#9a94ad" }}>No users found</td></tr>
            ) : users.map((u, i) => (
              <tr key={u.userId} style={{ background: i % 2 === 0 ? "#fff" : "#faf9fe", borderBottom: "1px solid #f0edf8" }}>
                <td style={{ padding: "10px 14px", fontWeight: 600 }}>{u.name}</td>
                <td style={{ padding: "10px 14px", color: "#6a6580", fontFamily: "monospace", fontSize: 12 }}>{u.email}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>{u.beginnerVideosWatched}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>{u.advanceVideosWatched}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700 }}>{u.demoTradesTaken}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: "#059669" }}>{u.demoWins}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: "#dc2626" }}>{u.demoLosses}</td>
                <td style={{ padding: "10px 14px", textAlign: "center", fontFamily: "monospace", fontWeight: 800, color: u.demoWinRate >= 62 ? "#059669" : "#6a6580" }}>
                  {u.demoTradesTaken > 0 ? `${u.demoWinRate}%` : "—"}
                </td>
                <td style={{ padding: "10px 14px", textAlign: "center" }}>{badge(u.demoTradingUnlocked, "Unlocked", "Locked", "#059669", "#a9a4bd")}</td>
                <td style={{ padding: "10px 14px", textAlign: "center" }}>{badge(u.continueWithoutCourse, "Skipped", "No", "#b45309", "#a9a4bd")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <Stack alignItems="center" style={{ marginTop: 24 }}>
          <Pagination
            count={Math.ceil(total / limit)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Stack>
      )}
    </div>
  );
}
